import React, { useState, useEffect } from "react";
import {
  Form,
  Select,
  Button,
  Modal,
  message,
  Divider,
  Row,
  Col,
  Space,
  Input,
  DatePicker,
  Alert,
  Radio,
} from "antd";
import { Api_Url } from "../../../config/config";
import moment from "moment";

const { Option } = Select;
const { TextArea } = Input;

// Constants from requirement sheet
const ASSET_TYPES = ["Laptop", "Desktop"];
const REASSIGN_OPTIONS = ["Branch", "Scrap"];

export const ItInventoryModalAdd = ({ onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [location, setLocation] = useState({ lat: null, lng: null });

  // Employee Search States (Mirroring Customer Search logic)
  const [searching, setSearching] = useState(false);
  const [searchAttempted, setSearchAttempted] = useState(false);
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [employeeSearchMode, setEmployeeSearchMode] = useState(true);

  useEffect(() => {
    fetchBranches();
    getGeolocation();
  }, [form]);

  const fetchBranches = async () => {
    try {
      const response = await fetch(
        `${Api_Url}api/v1/Branch?PageNumber=1&PageSize=1000`,
        {
          headers: {
            Authorization: `Bearer ${
              JSON.parse(sessionStorage.getItem("user")).accessToken
            }`,
          },
        }
      );
      const data = await response.json();
      const allBranches = Array.isArray(data.data) ? data.data : [];
      // Deduplicate by name, keeping the first branch record for each unique name
      const seen = new Set();
      const uniqueBranches = allBranches.filter((b) => {
        const key = b.name?.trim().toLowerCase();
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      setBranches(uniqueBranches);
    } catch (error) {
      message.error("Failed to load branches");
    }
  };

  const getGeolocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        setLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      });
    }
  };

  // Logic to find employee from UserProfiles table
  const searchEmployee = async () => {
    const branchId = form.getFieldValue("BranchId");
    const rawSearchValue = form.getFieldValue("EmployeeCodeSearch");
    const searchValue = rawSearchValue?.trim(); // Clean the input

    if (!branchId || !searchValue) {
      message.warning("Please select a branch and enter an Employee Code.");
      return;
    }

    setSearchAttempted(true);
    setSearching(true);

    try {
      // Endpoint logic assumed based on your UserProfile schema
      const response = await fetch(
        `${Api_Url}api/v1/UserProfile/bycode/${encodeURIComponent(
          searchValue
        )}`,
        {
          headers: {
            Authorization: `Bearer ${
              JSON.parse(sessionStorage.getItem("user")).accessToken
            }`,
          },
        }
      );
      const result = await response.json();

      if (response.ok && result.data) {
        setEmployeeDetails(result.data);
        form.setFieldsValue({
          EmployeeId: result.data.id,
          EmployeeName: `${result.data.firstName} ${
            result.data.lastName || ""
          }`,
          EmployeeDesignation: result.data.designationId, // Projected later in Repo
        });
        setEmployeeSearchMode(false);
        message.success("Employee found.");
      } else {
        message.info("No employee found with that code in this branch.");
      }
    } catch (error) {
      message.error("Error searching employee.");
    } finally {
      setSearching(false);
    }
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const formattedIssueDate = values.IssueDate.format("YYYY-MM-DD");
      // Using standard JSON post as no files are involved in basic IT inventory
      const payload = {
        ...values,
        IssueDate: formattedIssueDate,
        Latitude: String(location.lat || ""),
        Longitude: String(location.lng || ""),
        IsActive: true,
        IsScrap: false,
      };

      const response = await fetch(`${Api_Url}api/v1/ItInventory`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${
            JSON.parse(sessionStorage.getItem("user")).accessToken
          }`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error("Failed to save asset");

      message.success("IT Asset added successfully");
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <span style={{ fontSize: "20px", fontWeight: "bold" }}>
          Add IT Inventory Asset
        </span>
      }
      width={900}
      open={true}
      onCancel={onClose}
      footer={[
        <Space key="footer">
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="primary"
            loading={loading}
            onClick={() => form.submit()}
            disabled={employeeSearchMode}
          >
            Save Asset
          </Button>
        </Space>,
      ]}
      centered
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{ AssetType: "Laptop" }}
      >
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              name="BranchId"
              label="Branch"
              rules={[{ required: true }]}
            >
              <Select
                placeholder="Select Branch"
                showSearch
                filterOption={(input, option) =>
                  option?.label?.toLowerCase().includes(input.toLowerCase())
                }
                options={branches.map((b) => ({ label: b.name, value: b.id }))}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="AssetType"
              label="Asset Type"
              rules={[{ required: true }]}
            >
              <Radio.Group>
                {ASSET_TYPES.map((type) => (
                  <Radio.Button key={type} value={type}>
                    {type}
                  </Radio.Button>
                ))}
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left">Asset Information</Divider>
        <Row gutter={24}>
          <Col span={8}>
            <Form.Item
              name="SapCode"
              label="Fixed Asset - SAP Code"
              rules={[{ required: true }]}
            >
              <Input placeholder="Enter SAP Code" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="MakeModel"
              label="Make / Model"
              rules={[{ required: true }]}
            >
              <Input placeholder="e.g. Dell Latitude 5420" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="SerialNumber"
              label="Serial Number"
              rules={[{ required: true }]}
            >
              <Input placeholder="Enter S/N" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col span={24}>
            <Form.Item
              name="Description"
              label="Asset Description"
              rules={[
                {
                  required: true,
                  message: "Please provide a brief description",
                },
              ]}
            >
              <Input placeholder="e.g. 14-inch Laptop, 16GB RAM, 512GB SSD" />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left">Allocation Details</Divider>
        {employeeSearchMode ? (
          <Row gutter={24}>
            <Col span={24}>
              {/* ADD THE name="EmployeeCodeSearch" HERE */}
              <Form.Item
                name="EmployeeCodeSearch"
                label="Search Employee by Code"
                required
              >
                <Input
                  placeholder="Enter Employee Code and click search"
                  size="large"
                  suffix={
                    <Button
                      icon={<i className="fa-solid fa-magnifying-glass"></i>}
                      onClick={searchEmployee}
                      loading={searching}
                    />
                  }
                  onPressEnter={searchEmployee}
                />
              </Form.Item>
            </Col>
          </Row>
        ) : (
          <Row gutter={24} align="bottom">
            <Col span={10}>
              <Form.Item label="Employee Name">
                <Input value={form.getFieldValue("EmployeeName")} disabled />
              </Form.Item>
              <Form.Item name="EmployeeId" hidden>
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="IssueDate"
                label="Issue Date"
                rules={[{ required: true }]}
              >
                <DatePicker style={{ width: "100%" }} format="DD-MM-YYYY" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Button
                type="dashed"
                onClick={() => setEmployeeSearchMode(true)}
                block
                style={{ marginBottom: 24 }}
              >
                Change Employee
              </Button>
            </Col>
          </Row>
        )}

        <Row gutter={24}>
          <Col span={24}>
            <Form.Item name="Remarks" label="Remarks">
              <TextArea
                rows={2}
                placeholder="Add any additional notes here..."
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};
